import {
    ActionIcon,
    Anchor,
    Badge,
    Box,
    Checkbox,
    Group,
    NavLink,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { IconChevronsDown, IconChevronsUp, IconSearch } from '@tabler/icons-react';
import { memo } from 'react';
import { SYNC_STATUS } from '../../domain/status.js';
import { TypeIcon } from '../TypeGlyph/TypeIcon.js';
import {
    cappedNote,
    fieldGroups,
    filteredNote,
    RECORD_LIST_LIMIT,
    recordName,
    type RecordEntry,
} from './pushRecordsView.js';
import { previewSentence, type PushPreview } from './pushSummary.js';
import type { RecordsView } from './useRecordsView.js';

/**
 * Every environment the push could go to, each with its dry-run sentence — tick to include it.
 * A refused environment cannot be ticked; its sentence says why.
 */
export const EnvironmentChecklist = ({
    names,
    previews,
    previewPending,
    ticked,
    onToggle,
    advisory,
}: {
    names: string[];
    previews: ReadonlyMap<string, PushPreview>;
    previewPending: boolean;
    ticked: ReadonlySet<string>;
    onToggle: (name: string) => void;
    advisory: (name: string) => string | null;
}) => (
    <Stack gap={4} px="xs">
        {names.map((name) => {
            const preview = previews.get(name);
            const line = previewPending && !preview ? 'checking…' : preview ? previewSentence(preview) : '';
            const warn = advisory(name);
            return (
                <Group
                    key={name}
                    gap="xs"
                    wrap="nowrap"
                    align="flex-start"
                    p={6}
                    style={{
                        borderRadius: 'var(--mantine-radius-md)',
                        background: ticked.has(name) ? 'var(--mantine-color-rgsOrange-light)' : undefined,
                    }}
                >
                    <Checkbox
                        size="xs"
                        mt={2}
                        checked={ticked.has(name)}
                        onChange={() => onToggle(name)}
                        disabled={preview !== undefined && preview.refused !== null && !ticked.has(name)}
                        aria-label={`Push to ${name}`}
                    />
                    <Box style={{ minWidth: 0 }}>
                        <Text className="rgs-numeric" fz="sm" fw={600}>
                            {name}
                        </Text>
                        <Text fz={11} c={preview?.refused ? 'red' : 'dimmed'}>
                            {line}
                        </Text>
                        {warn && (
                            <Text fz={11} c="dimmed">
                                {warn}
                            </Text>
                        )}
                    </Box>
                </Group>
            );
        })}
    </Stack>
);

/** A count of changes, in the drift ink — the grids' own, so "changed" reads the same everywhere. */
const ChangeCount = ({ count }: { count: number }) => (
    <Badge size="xs" radius="sm" variant="light" color={SYNC_STATUS.drift.color} aria-label={`${count} changed`}>
        {count}
    </Badge>
);

/**
 * The records the push writes to the environment on screen, each with its count of changed
 * fields or its `create` tag — a studio's games a step in under it — found by a search over
 * titles and keys and narrowed to the records that change one field. Picking one brings it to
 * the pane; the collapse control folds every studio's own games under it at once.
 */
export const RecordNav = ({ view }: { view: RecordsView }) => (
    <Stack gap={4} px="xs">
        <Stack gap={4} py={4}>
            <Group gap={6} wrap="nowrap">
                <TextInput
                    size="xs"
                    variant="filled"
                    placeholder="Find a record"
                    aria-label="Find a record"
                    leftSection={<IconSearch size={13} />}
                    value={view.query}
                    onChange={(event) => view.setQuery(event.currentTarget.value)}
                    style={{ flex: 1 }}
                />
                {view.entries.some((entry) => entry.child) && (
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={view.allCollapsed ? 'Expand all' : 'Collapse all'}
                        onClick={view.allCollapsed ? view.expandAll : view.collapseAll}
                    >
                        {view.allCollapsed ? (
                            <IconChevronsDown size={16} stroke={1.7} />
                        ) : (
                            <IconChevronsUp size={16} stroke={1.7} />
                        )}
                    </ActionIcon>
                )}
            </Group>
            <FieldFilter view={view} />
            <NarrowedNote view={view} />
        </Stack>
        <Box>
            {view.shown.slice(0, RECORD_LIST_LIMIT).map((entry) => (
                <RecordNavItem
                    key={entry.id}
                    entry={entry}
                    active={entry.id === view.active}
                    nested={view.nested.has(entry.id)}
                    onPick={view.jump}
                />
            ))}
            {view.shown.length > RECORD_LIST_LIMIT && (
                <Text fz="xs" c="dimmed" px="sm" py={6}>
                    {cappedNote(RECORD_LIST_LIMIT, view.shown.length)}
                </Text>
            )}
        </Box>
    </Stack>
);

/** One record in the nav — memoised, since the record the pane is at changes as it is picked. */
const RecordNavItem = memo(
    ({
        entry,
        active,
        nested,
        onPick,
    }: {
        entry: RecordEntry;
        active: boolean;
        nested: boolean;
        onPick: (id: string) => void;
    }) => {
        const { name, isKey } = recordName(entry);
        const changed = Object.keys(entry.op.patch).length;
        return (
            <NavLink
                component="button"
                active={active}
                variant="light"
                onClick={() => onPick(entry.id)}
                aria-label={`${name}, ${entry.op.op === 'create' ? 'create' : `update, ${changed} changed`}`}
                leftSection={<TypeIcon type={entry.op.entityType} size={14} stroke={1.7} aria-hidden />}
                label={
                    <Text fz="sm" truncate="end" className={isKey ? 'rgs-numeric' : undefined}>
                        {name}
                    </Text>
                }
                rightSection={
                    entry.op.op === 'create' ? (
                        <Badge size="xs" variant="light" color="gray" radius="sm" tt="none">
                            create
                        </Badge>
                    ) : (
                        <ChangeCount count={changed} />
                    )
                }
                py={5}
                pl={nested ? 26 : undefined}
                style={{ borderRadius: 'var(--mantine-radius-md)' }}
            />
        );
    },
);

/** `4 of 6 records · Clear` while the search or the field filter hides some; nothing otherwise. */
export const NarrowedNote = ({ view }: { view: RecordsView }) => {
    const narrowed = filteredNote(view.visible.length, view.entries.length);
    if (!narrowed) return null;
    return (
        <Text fz="xs" c="dimmed" px={4}>
            {narrowed} ·{' '}
            <Anchor component="button" type="button" fz="xs" onClick={view.clear}>
                Clear
            </Anchor>
        </Text>
    );
};

/**
 * "Only records changing <field>", over the fields the push on screen changes — grouped by
 * type only when it writes more than one. A create changes no field, so a picked field leaves
 * creates out, and the note above the list counts what it hides.
 */
export const FieldFilter = ({ view }: { view: RecordsView }) => {
    const groups = fieldGroups(view.fields);
    const counts = new Map(view.fields.map((option) => [option.id, option.records]));
    if (view.fields.length === 0) return null;
    const data =
        groups.length === 1 && groups[0]!.group === ''
            ? groups[0]!.items.map((item) => ({ value: item.id, label: item.label }))
            : groups.map(({ group, items }) => ({
                  group,
                  items: items.map((item) => ({ value: item.id, label: item.label })),
              }));
    return (
        <Select
            size="xs"
            aria-label="Only records changing"
            placeholder="Only records changing…"
            clearable
            searchable
            data={data}
            value={view.field}
            onChange={view.setField}
            renderOption={({ option }) => (
                <Group justify="space-between" wrap="nowrap" w="100%" gap="xs">
                    <Text fz="xs">{option.label}</Text>
                    <Text fz={11} c="dimmed" className="rgs-numeric">
                        {counts.get(option.value)}
                    </Text>
                </Group>
            )}
        />
    );
};
